import type { EntityState, EntityId } from './EntityState';

/**
 * Describes a partial set of changes to apply when updating an entity
 */
export type EntityChanges<T, Id extends EntityId> = { id: Id; changes: Partial<T> };

/**
 * A set of functions for safely updating an entity's normalized state.
 */
export interface EntityStateUpdaters<T, Id extends EntityId> {
  /** Add one entity, rejecting those with preexisting IDs */
  addOne<S extends EntityState<T, Id>>(state: S, entity: T): S;
  /** Add many entities, rejecting those with preexisting IDs */
  addMany<S extends EntityState<T, Id>>(state: S, entities: readonly T[] | Record<Id, T>): S;
  /** Set one entity, overwriting existing entities */
  setOne<S extends EntityState<T, Id>>(state: S, entity: T): S;
  /** Set many entities, overwriting existing entities */
  setMany<S extends EntityState<T, Id>>(state: S, entities: readonly T[] | Record<Id, T>): S;
  /** Set the entities to the new list, removing others */
  setAll<S extends EntityState<T, Id>>(state: S, entities: readonly T[] | Record<Id, T>): S;
  /** Remove one entity by its ID */
  removeOne<S extends EntityState<T, Id>>(state: S, key: Id): S;
  /** Remove many entities by their IDs */
  removeMany<S extends EntityState<T, Id>>(state: S, keys: readonly Id[]): S;
  /** Remove all entities */
  removeAll<S extends EntityState<T, Id>>(state: S): S;
  /** Update one entity by shallow merging an update containing a partial set of properties */
  updateOne<S extends EntityState<T, Id>>(state: S, update: EntityChanges<T, Id>): S;
  /** Update many entities by shallow merging updates, each containing a partial set of properties */
  updateMany<S extends EntityState<T, Id>>(state: S, updates: EntityChanges<T, Id>[]): S;
}

/**
 * Options for configuring {@link EntityStateUpdaters}
 */
export type EntityStateUpdatersOptions<T, Id extends EntityId> = {
  /** Get the ID from a model */
  selectId: (model: T) => Id;
  /** Create the initial state for an entity */
  createInitialState: () => EntityState<T, Id>;
};

/**
 * Creates a set of {@link EntityStateUpdaters} for managing a collection of entities.
 */
export const createEntityStateUpdaters = <T, Id extends EntityId>(
  options: EntityStateUpdatersOptions<T, Id>,
): EntityStateUpdaters<T, Id> => {
  const { selectId, createInitialState } = options;

  // Note: we don't care what the initial state is, we just need to
  // extract the entity portions from it. We can ignore the rest to
  // ensure we never overwrite it by accident.
  const createEntityState = () => {
    const { ids, entities } = createInitialState();
    return { ids, entities };
  };

  const addOne = <State extends EntityState<T, Id>>(
    state: State,
    entity: T | null | undefined,
  ): State => {
    if (!entity) {
      return state;
    }

    const id = selectId(entity);
    if (id in state.entities) {
      return state;
    }

    return {
      ...state,
      ids: Array.from(new Set([...state.ids, id])),
      entities: {
        ...state.entities,
        [id]: entity,
      },
    };
  };

  const addMany = <State extends EntityState<T, Id>>(
    state: State,
    entities: readonly T[] | Record<Id, T>,
  ): State => ensureEntitiesArray(entities).reduce(addOne, state);

  const setOne = <State extends EntityState<T, Id>>(
    state: State,
    entity: T | null | undefined,
  ): State => {
    if (!entity) {
      return state;
    }

    const id = selectId(entity);
    const ids = id in state.entities ? state.ids : Array.from(new Set([...state.ids, id]));
    const entities = { ...state.entities, [id]: entity };
    return { ...state, ids, entities };
  };

  const setMany = <State extends EntityState<T, Id>>(
    state: State,
    entities: readonly T[] | Record<Id, T>,
  ): State => ensureEntitiesArray(entities).reduce(setOne, state);

  const setAll = <State extends EntityState<T, Id>>(
    state: State,
    entities: readonly T[] | Record<Id, T>,
  ): State =>
    addMany(
      {
        ...state,
        ...createEntityState(),
      },
      entities,
    );

  const removeOne = <State extends EntityState<T, Id>>(state: State, id: Id): State => {
    if (!(id in state.entities)) {
      return state;
    }

    const entityState = createEntityState();

    for (const entityId of state.ids) {
      if (entityId !== id) {
        entityState.ids.push(entityId);
        entityState.entities[entityId] = state.entities[entityId];
      }
    }

    return {
      ...state,
      ...entityState,
    };
  };

  const removeMany = <State extends EntityState<T, Id>>(state: State, ids: readonly Id[]): State =>
    ids.reduce(removeOne, state);

  const removeAll = <State extends EntityState<T, Id>>(state: State): State => ({
    ...state,
    ...createEntityState(),
  });

  const updateOne = <State extends EntityState<T, Id>>(
    state: State,
    update: EntityChanges<T, Id>,
  ): State => {
    const id = update.id;
    if (!(id in state.entities)) {
      return state;
    }

    const changes = update.changes;
    const entity = state.entities[id];
    const updatedEntity = { ...entity, ...changes };

    if (entity === updatedEntity) {
      return state;
    }

    return setOne(state, updatedEntity);
  };

  const updateMany = <State extends EntityState<T, Id>>(
    state: State,
    updates: EntityChanges<T, Id>[],
  ): State => updates.reduce((acc, update) => updateOne(acc, update), state);

  return {
    addMany,
    addOne,
    removeAll,
    removeMany,
    removeOne,
    setAll,
    setMany,
    setOne,
    updateOne,
    updateMany,
  };
};

/**
 * A small utility to ensure that a record or collection of entities are converted to array form.
 */
function ensureEntitiesArray<T, Id extends EntityId>(
  entities: readonly T[] | Record<Id, T>,
): readonly T[] {
  if (!Array.isArray(entities)) {
    return Object.values(entities);
  }

  return entities;
}
